// The firmware act, Upgrade (ADR-0236, built by #1358, as ADR-0367 amends it; its gates and its
// hand-off built by #1744; the one firmware act since ADR-0404, #1816). **Since ADR-0413 (#1832)
// it is one target, one consequence line and one button**: the entries the server offers for this
// access point, named by their build, of which the operator picks one and clicks once. That click
// approves a delivery the firmware rollout performs, and it is the acceptance of everything the
// entry asks: the request carries each acceptance `entry.acceptances` lists, set to the entry's
// exact predicted build, as the accepted request always has (ADR-0259 `2c`). Nothing on screen
// names those acceptances (ADR-0413 `1d`); the server's gates still refuse a request without them.
//
// **The suggestion is the server's and is only a preselection** (ADR-0404 `2.1d`, decision 4). The
// act lists only entries Zenith qualifies (ADR-0413 `1.1a`): an unqualified build is not offered
// here, though an integrator may still request one with `accept_firmware`. It reads the server's
// own `qualified` field: this file orders nothing, and a version string never picks an entry here.
//
// **Nothing in the act is the source of truth** (ADR-0413 decision 10). It reads both queries
// each time it mounts, keeps no acceptance and no draft, and closes at any time — the card's × or
// Escape — without cancelling anything: the delivery is the rollout's once handed over, and the
// page's write lane finishes a request already sent and reports it.
//
// **What it shows is the server's and nothing more** (§18). The build string arrives with the
// incumbent cloud's name already removed at `/v1`; this file removes nothing. It never names a
// Kiln status, a rollback target or a way back (ADR-0413 `3a`, `11a`).
//
// **And while it is open it shows only the act** (#1839, Gabriel Provencher's markup of staging's
// W003-GARAGE on 2026-10-16, recorded as dated notes on ADR-0413 `2a`, `3a`, `4a`, `6a` and `10a`):
// the consequence line, the builds and the button. No *Details*, no refusal line under an entry, no
// *Recorded against your name*.
//
// **Since #1844 it says nothing about the suggestion and is not drawn while a delivery is under way**
// (Gabriel Provencher on W001-BASEMENT, 2026-10-17: *"the progress disappeared !"*; dated notes on
// ADR-0413 `3a` and `6a`). The *Suggested* tag is the whole of the suggestion; *No firmware is
// suggested…* in its three forms is gone. During a delivery the card shows its progress instead, and
// should the act's own read see one first, it offers and labels no entry. **Since #1845 the force is
// not the act's**: the refused delivery's card sends it in one click.
//
// **Since #1841 it is drawn in one place: the act of the device page's one Firmware card**
// (`FirmwareCard.tsx`, under the card's title and ×; Gabriel Provencher, 2026-10-17, *"we should
// have only a single firmware upgrade/delivery card"*). It has no frame, title or Cancel of its own,
// and the site's table no longer offers it (*"remove the upgrade option from this table"*, the same
// day). Dated notes on ADR-0413 `6a`, `8a` and `10a` and ADR-0403 `1a`.
import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { useQuery } from "../../api/hooks";
import type { useMutation } from "../../api/mutation";
import { headline, type Problem } from "../../api/problem";
import type { components } from "../../api/schema";
import { Loading, Pill, ProblemBox } from "../../ui";
import { sendFirmwareRequest, type Device, type FirmwareAnswer, type FirmwareDelivery, type FirmwareRequestAcceptance } from "./commands";
import { markerOf, useDeliveryChanges } from "./delivery";

type Options = components["schemas"]["DeviceFirmwareRequestOptions"];
type Deliveries = components["schemas"]["DeviceFirmwareDeliveries"];

// Restates ADR-0413 decision 2 (`2a`) as amended 2026-10-16 (#1839) — a refusal in the act is one
// line in the operator's words, and that is all: the server's message and the request id are not in
// the act. A vendor-line refusal means the device's line moved since the act read it: the next
// click carries that acceptance, as the first would have. A reason with no line of its own reads its
// lead, still one line.
function refusalLine(p: Problem, name: string): string {
  switch (p.reason) {
    case "firmware_line_change": return `${name}'s firmware changed since this was read. Upgrade again to go ahead.`;
    case "unqualified_firmware": return `This build is not supported for ${name}.`;
    case "firmware_delivery_in_flight": return `An upgrade of ${name} is already under way.`;
    case "firmware_delivery_not_handed_over": return `The firmware rollout did not take the upgrade of ${name}. Nothing was sent.`;
    default: return headline(p);
  }
}

/** The act itself, with no frame: the consequence line, the builds and the one button. Escape calls `onClose` as the card's × does. */
export function FirmwareAct({ device, m, onAnswer, onClose }: {
  device: Device;
  /** The page's one write lane (#189). */
  m: ReturnType<typeof useMutation>;
  /** The server answered `accepted`, `refused` or `recorded`. Not called for a Problem: that stays
   * in the act as its one line (decision 2). */
  onAnswer(answer: FirmwareAnswer): void;
  onClose(): void;
}) {
  const options = useQuery<Options>(() => api.GET("/devices/{serial}/firmware-request-options", { params: { path: { serial: device.serial } } }), [device.serial]);
  const deliveries = useQuery<Deliveries>(() => api.GET("/devices/{serial}/firmware-deliveries", { params: { path: { serial: device.serial } } }), [device.serial]);
  const newest: FirmwareDelivery | undefined = deliveries.status === "ready" ? deliveries.data.deliveries[0] : undefined;
  const reloadDeliveries = deliveries.reload;
  // The change stream says a delivery moved (ADR-0371 `d.1a`); the act re-reads through its own
  // read. No `EventSource` is a stream that is down, and the act then shows what it last read.
  useDeliveryChanges(reloadDeliveries);
  const [chosen, setChosen] = useState<string>();
  const shown = options.status === "ready" ? options.data : undefined;
  // Restates ADR-0413 decision 1.1 (`1.1a`), narrowing ADR-0404 decisions 2.2a and 2.3a, ADR-0367
  // decision 2.3a and ADR-0037 decision 2 in the act — only a firmware this service qualifies, and
  // that predicts a build, is offered; an unsupported one is not listed and not mentioned.
  //
  // Restates ADR-0413 decision 6 (`6a`) as amended 2026-10-17 (#1844) — while a delivery is under way
  // the act offers and labels no entry: the card shows the delivery instead, and an entry's
  // *Already running* then reads the build the device came back on before the rollout confirmed it.
  // This is the act's own read catching a delivery the page's read has not yet.
  const underWay = !!newest && markerOf(newest.state) === "under_way";
  const entries = !underWay && shown?.catalog.state === "read" ? shown.entries.filter((e) => e.qualified && !!e.predicted_firmware) : [];
  // Preselected only where the server suggests it (ADR-0404 `2.1d`).
  const suggested = shown?.suggestion.state === "suggested" ? shown.suggestion.artifact : undefined;
  const pick = entries.find((e) => e.artifact === (chosen ?? suggested));
  // A refusal of the line the device moved to since the read: the next click carries it.
  const lineAgain = m.problem?.reason === "firmware_line_change" ? m.problem.predicted_firmware : undefined;
  // **The force is not the act's** since #1845 (Gabriel Provencher, 2026-10-17: *"Why do I have to
  // click twice on upgrade anyway, on two different screens ?"*; dated notes on ADR-0413 `5d` and
  // `7.3a` and ADR-0411 `9b`): the refused delivery's own card sends it in one click
  // (`FirmwareCard.tsx`). An entry whose next step is `force_available` is offered here as any other
  // entry is, and its plain request is refused or not by the server as before (ADR-0413 `9a`).
  // Escape closes the act wherever focus is, as the × does (ADR-0413 `10a`).
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => { if (ev.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const go = async () => {
    if (!pick?.predicted_firmware) return;
    const accept: FirmwareRequestAcceptance = {};
    if (pick.acceptances.includes("firmware_line_change") || lineAgain) accept.line = lineAgain ?? pick.predicted_firmware;
    const answer = await sendFirmwareRequest(m, device, pick.artifact, accept);
    reloadDeliveries();
    if (answer) onAnswer(answer);
  };
  return (
    <>
      {/* Restates ADR-0413 decision 4 (`4a`) — one consequence line and nothing else before the click. */}
      <p className="muted">{device.name} reboots, and its clients drop until it is back.</p>
      {options.status === "loading" && <Loading label="Loading the firmware for this device" />}
      {options.status === "error" && <ProblemBox problem={options.problem} fold />}
      {shown?.catalog.state === "unavailable" && <p className="warn">The firmware for {device.name} could not be read just now.</p>}
      {!underWay && shown?.catalog.state === "read" && entries.length === 0 && <p className="muted">No supported firmware for {device.name} yet.</p>}
      {entries.length > 0 && (
        <fieldset className="field">
          <legend className="sr-only">Firmware</legend>
          {entries.map((e) => (
            <div key={e.artifact} className="firmware-entry">
              <label className="approve">
                <input type="radio" name="firmware-request" value={e.artifact} checked={pick?.artifact === e.artifact} onChange={() => setChosen(e.artifact)} />
                {/* Restates ADR-0404 decisions 2.1d and 4, as ADR-0413 decision 3 (`3a`) draws them — the
                    build is the label; the one suggested firmware is tagged, and one the device already runs
                    is tagged, never suggested, and may still be picked. */}
                <span>{e.predicted_firmware}{e.artifact === suggested && <> <Pill tone="info">Suggested</Pill></>}{e.already_running && <> <Pill>Already running</Pill></>}</span>
              </label>
              {/* Restates ADR-0413 decision 3 (`3a`) as amended 2026-10-16 (#1839) — the build is the whole
                  entry: its digest and qualification verdict are not drawn in the act (ADR-0367
                  decision 2.3a and ADR-0037 decisions 1 and 2 as drawn), and no rollback target is
                  named (decision 11). */}
            </div>
          ))}
        </fieldset>
      )}
      {m.problem && <p role="alert" className="warn">{refusalLine(m.problem, device.name)}</p>}
      {!underWay && (
        <div className="actions">
          {/* Restates ADR-0413 decision 1 (`1d`), re-ruling how ADR-0259 decision 5a and ADR-0367 decision
              2.2a are shown — the one button names the target, and its click is the acceptance of what the
              entry asks; nothing else on screen names it. */}
          <button type="button" className="btn btn-primary" disabled={m.busy || !pick} onClick={() => void go()}>
            {pick ? `Upgrade to ${pick.predicted_firmware}` : "Upgrade"}
          </button>
        </div>
      )}
      {/* Restates ADR-0413 decision 4 (`4a`) as amended 2026-10-16 (#1839) — nothing is said under the
          buttons: the click is recorded against the person, and the act does not say so. */}
    </>
  );
}
